// src/components/modals/DeleteProductModal.jsx
import { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { X, Loader2, AlertTriangle, Info } from 'lucide-react'
import ProductService from '../../services/productService'

const DeleteProductModal = ({ isOpen, onClose, product, onProductDeleted, onProductDisabled }) => {
  const [loading, setLoading] = useState(false)
  const [checkingAssignment, setCheckingAssignment] = useState(false)
  const [error, setError] = useState(null)
  const [assignmentStatus, setAssignmentStatus] = useState(null)
  const [canDelete, setCanDelete] = useState(false)

  // Check product assignment when modal opens
  useEffect(() => {
    if (isOpen && product?._id) {
      checkProductAssignment()
    }
  }, [isOpen, product])

  // Check if product is assigned to branches
  const checkProductAssignment = async () => {
    try {
      setCheckingAssignment(true)
      setError(null)
      setAssignmentStatus(null)
      
      console.log('🔍 Checking product assignment for:', product._id)
      const response = await ProductService.checkProductAssignment(product._id)
      
      if (response && response.success) {
        const assignment = response.data
        setAssignmentStatus(assignment)
        setCanDelete(!assignment.isAssigned)
        
        console.log('✅ Assignment check completed:', assignment)
      } else {
        console.warn('⚠️ Failed to check assignment:', response?.message)
        setCanDelete(false)
        setError('Failed to check product assignment status')
      }
    } catch (error) {
      console.error('❌ Error checking product assignment:', error)
      setCanDelete(false)
      setError('Error checking if product can be deleted')
    } finally {
      setCheckingAssignment(false)
    }
  }

  // Handle product deletion
  const handleDelete = async () => {
    if (!product?._id) {
      setError('Product ID is missing')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      console.log('🗑️ Deleting product:', product._id)
      const response = await ProductService.deleteProduct(product._id)
      
      if (response && response.success) {
        console.log('✅ Product deleted successfully')
        onProductDeleted(product)
        handleClose()
      } else {
        console.error('⚠️ Failed to delete product:', response?.message)
        setError(response?.message || 'Failed to delete product')
      }
    } catch (error) {
      console.error('❌ Error deleting product:', error)
      
      // Handle specific error types
      let errorMessage = 'Failed to delete product. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Product not found. It may have already been deleted.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to delete this product. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message.includes('conflict') || error.message.includes('in use')) {
        errorMessage = 'Cannot delete product. It is currently in use by branches.'
      } else if (error.message.includes('assigned') || error.message.includes('branch')) {
        errorMessage = 'Cannot delete product. Please remove it from all branches first.'
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Handle product disable (status toggle)
  const handleDisable = async () => {
    if (!product?._id) {
      setError('Product ID is missing')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      console.log('🔄 Disabling product:', product._id)
      const response = await ProductService.toggleProductStatus(product._id, false)
      
      if (response && response.success) {
        console.log('✅ Product disabled successfully')
        const updatedProduct = { ...product, isActive: false }
        onProductDisabled(updatedProduct)
        handleClose()
      } else {
        console.error('⚠️ Failed to disable product:', response?.message)
        setError(response?.message || 'Failed to disable product')
      }
    } catch (error) {
      console.error('❌ Error disabling product:', error)
      
      let errorMessage = 'Failed to disable product. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Product not found. It may have been deleted.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to modify this product. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    if (!loading && !checkingAssignment) {
      setError(null)
      setAssignmentStatus(null)
      setCanDelete(false)
      onClose()
    }
  }

  // Don't render if not open or no product
  if (!isOpen || !product) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {canDelete ? 'Delete Product' : 'Product Action Required'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={loading || checkingAssignment}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            {/* Loading State */}
            {checkingAssignment && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-gray-600">Checking product assignment...</span>
              </div>
            )}

            {/* Error Message */}
            {error && !checkingAssignment && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Assignment Status and Actions */}
            {!checkingAssignment && assignmentStatus && (
              <>
                {/* Product Details */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <h4 className="font-medium text-gray-900 mb-1">{product.name}</h4>
                  <p className="text-sm text-gray-600">SKU: {product.sku}</p>
                  {product.categoryDetails && (
                    <p className="text-sm text-gray-600">Category: {product.categoryDetails.name}</p>
                  )}
                </div>

                {/* Can Delete */}
                {canDelete && (
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-700 mb-2">
                          This product is not assigned to any branches and can be safely deleted.
                        </p>
                        <p className="text-sm font-medium text-red-600">
                          Are you sure you want to delete this product? This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cannot Delete - Show Assignment Info */}
                {!canDelete && assignmentStatus.isAssigned && (
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-gray-700 mb-2">
                          This product cannot be deleted because it is assigned to{' '}
                          <span className="font-medium">
                            {assignmentStatus.branchCount} branch{assignmentStatus.branchCount !== 1 ? 'es' : ''}
                          </span>.
                        </p>
                        
                        {assignmentStatus.branches && assignmentStatus.branches.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-700 mb-1">Assigned branches:</p>
                            <div className="flex flex-wrap gap-1">
                              {assignmentStatus.branches.slice(0, 3).map((branch, index) => (
                                <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                  {branch.name}
                                </span>
                              ))}
                              {assignmentStatus.branches.length > 3 && (
                                <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                                  +{assignmentStatus.branches.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-700">
                          You can disable this product instead, which will make it inactive but preserve the data.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Action Buttons */}
          {!checkingAssignment && assignmentStatus && (
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
                className="flex-1"
              >
                Cancel
              </Button>
              
              {canDelete ? (
                <Button
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Product'
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleDisable}
                  disabled={loading}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Disabling...
                    </>
                  ) : (
                    'Disable Product'
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeleteProductModal